/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

import {
    $isListNode,
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    ListNode,
    REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isDecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import type { HeadingTagType } from "@lexical/rich-text";
import { $createHeadingNode, $isHeadingNode, $isQuoteNode } from "@lexical/rich-text";
import { $getSelectionStyleValueForProperty, $isAtNodeEnd, $patchStyleText, $setBlocksType } from "@lexical/selection";
import { $isTableNode, $isTableSelection } from "@lexical/table";
import {
    $findMatchingParent,
    $getNearestBlockElementAncestorOrThrow,
    $getNearestNodeOfType,
    $insertNodeToNearestRoot,
    mergeRegister,
} from "@lexical/utils";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import InsertPageBreakOutlinedIcon from "@mui/icons-material/InsertPageBreakOutlined";
import KeyboardDoubleArrowUpOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowUpOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import classNames from "classnames";
import type { Action } from "components/shared/ButtonMenu";
import ButtonMenu from "components/shared/ButtonMenu";
import StyledButton from "components/shared/StyledButton";
import { PortalType } from "contexts/portal.context";
import { useCompanySettings } from "hooks/useCompanySettings";
import type {
    ElementFormatType,
    ElementNode,
    LexicalEditor,
    LexicalNode,
    ParagraphNode,
    RangeSelection,
    TextNode,
} from "lexical";
import {
    $createLineBreakNode,
    $createParagraphNode,
    $createTextNode,
    $getSelection,
    $insertNodes,
    $isElementNode,
    $isNodeSelection,
    $isParagraphNode,
    $isRangeSelection,
    $isRootOrShadowRoot,
    $isTextNode,
    $setSelection,
    CAN_REDO_COMMAND,
    CAN_UNDO_COMMAND,
    COMMAND_PRIORITY_CRITICAL,
    FORMAT_ELEMENT_COMMAND,
    FORMAT_TEXT_COMMAND,
    REDO_COMMAND,
    SELECTION_CHANGE_COMMAND,
    UNDO_COMMAND,
} from "lexical";
import { ReactNode, RefObject, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import i18n from "../../../../i18n";
import { INSERT_ARTICLE_COMMAND } from "../ArticlePlugin";
import { $isArticleContainerNode } from "../ArticlePlugin/ArticleContainerNode";
import { $createArticleContentNode, $isArticleContentNode } from "../ArticlePlugin/ArticleContentNode";
import { $createConditionalNode, $isConditionalNode, ConditionalNode } from "../ConditionalNode";
import { INSERT_CONDITIONAL_COMMAND } from "../ConditionalPlugin";
import {
    $createConditionalContainerNode,
    $isConditionalContainerNode,
} from "../ConditionalPlugin/ConditionalContainerNode";
import { $isConditionalContentNode, ConditionalContentNode } from "../ConditionalPlugin/ConditionalContentNode";
import DropDown, { DropDownItem } from "../DropDown";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { INSERT_PAGE_BREAK } from "../plugins/PageBreakPlugin";
import { INSERT_IMAGE_COMMAND } from "../plugins/ImagesPlugin";
import InsertImageDialog from "../InsertImageDialog";
import { $isImageNode } from "../nodes/ImageNode";
import { INSERT_REPEATABLE_COMMAND } from "../RepeatablePlugin";
import { $isRepeatableContainerNode } from "../RepeatablePlugin/RepeatableContainerNode";
import { $isRepeatableContentNode, RepeatableContentNode } from "../RepeatablePlugin/RepeatableContentNode";
import type { TBlockAttribute, TLexicalBlockType } from "../utils";
import FontSize from "./fontSize";
import ToolbarButton from "./ToolbarButton";

const CAN_USE_DOM: boolean =
    typeof window !== "undefined" &&
    typeof window.document !== "undefined" &&
    typeof window.document.createElement !== "undefined";

const IS_APPLE: boolean = CAN_USE_DOM && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

export function getSelectedNode(selection: RangeSelection): TextNode | ElementNode {
    const anchor = selection.anchor;
    const focus = selection.focus;
    const anchorNode = selection.anchor.getNode();
    const focusNode = selection.focus.getNode();
    if (anchorNode === focusNode) {
        return anchorNode;
    }
    const isBackward = selection.isBackward();
    if (isBackward) {
        return $isAtNodeEnd(focus) ? anchorNode : focusNode;
    } else {
        return $isAtNodeEnd(anchor) ? anchorNode : focusNode;
    }
}

const blockTypeToBlockName = {
    bullet: i18n.t("RichEditor.BulletList"),
    check: "Check List",
    code: "Code Block",
    h1: i18n.t("RichEditor.Heading1"),
    h2: i18n.t("RichEditor.Heading2"),
    h3: i18n.t("RichEditor.Heading3"),
    h4: "Heading 4",
    h5: "Heading 5",
    h6: "Heading 6",
    number: i18n.t("RichEditor.NumberedList"),
    paragraph: i18n.t("RichEditor.Normal"),
    quote: "Quote",
    "article-container": "Article",
    conditional: i18n.t("RichEditor.Optional"),
};

const rootTypeToRootName = {
    root: "Root",
    table: "Table",
};

const ELEMENT_FORMAT_OPTIONS: {
    [key in Exclude<ElementFormatType, "">]: {
        icon: string;
        iconRTL: string;
        name: string;
    };
} = {
    center: {
        icon: "center-align",
        iconRTL: "center-align",
        name: i18n.t("RichEditor.CenterAlign"),
    },
    end: {
        icon: "right-align",
        iconRTL: "left-align",
        name: i18n.t("RichEditor.RightAlign"),
    },
    justify: {
        icon: "justify-align",
        iconRTL: "justify-align",
        name: i18n.t("RichEditor.Justify"),
    },
    left: {
        icon: "left-align",
        iconRTL: "left-align",
        name: i18n.t("RichEditor.LeftAlign"),
    },
    right: {
        icon: "right-align",
        iconRTL: "right-align",
        name: i18n.t("RichEditor.RightAlign"),
    },
    start: {
        icon: "left-align",
        iconRTL: "right-align",
        name: i18n.t("RichEditor.LeftAlign"),
    },
};

function dropDownActiveClass(active: boolean) {
    if (active) return "active dropdown-item-active";
    else return "";
}

function BlockFormatDropDown({
    editor,
    blockType,
    rootType,
    disabled = false,
}: {
    blockType: keyof typeof blockTypeToBlockName;
    rootType: keyof typeof rootTypeToRootName;
    editor: LexicalEditor;
    disabled?: boolean;
}): JSX.Element {
    const formatParagraph = () => {
        editor.update(() => {
            const selection = $getSelection();
            $setBlocksType(selection, () => $createParagraphNode());
        });
    };

    const formatHeading = (headingSize: HeadingTagType) => {
        if (blockType !== headingSize) {
            editor.update(() => {
                const selection = $getSelection();
                $setBlocksType(selection, () => $createHeadingNode(headingSize));
            });
        }
    };

    const formatBulletList = () => {
        if (blockType !== "bullet") {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        } else {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        }
    };

    const formatNumberedList = () => {
        if (blockType !== "number") {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
        } else {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        }
    };

    return (
        <DropDown
            disabled={disabled || ["Article", i18n.t("RichEditor.Optional")].includes(blockTypeToBlockName[blockType])}
            buttonClassName="toolbar-item block-controls"
            buttonIconClassName={"icon block-type " + blockType}
            buttonLabel={blockTypeToBlockName[blockType]}
            buttonAriaLabel="Formatting options for text style"
        >
            <DropDownItem
                className={"item " + dropDownActiveClass(blockType === "paragraph")}
                onClick={formatParagraph}
            >
                {/* <i className="icon paragraph" /> */}
                <span className="text">{i18n.t("RichEditor.Normal")}</span>
            </DropDownItem>
            <DropDownItem
                className={"item " + dropDownActiveClass(blockType === "h1")}
                onClick={() => formatHeading("h1")}
            >
                {/* <i className="icon h1" /> */}
                <span className="text">{i18n.t("RichEditor.Heading1")}</span>
            </DropDownItem>
            <DropDownItem
                className={"item " + dropDownActiveClass(blockType === "h2")}
                onClick={() => formatHeading("h2")}
            >
                {/* <i className="icon h2" /> */}
                <span className="text">{i18n.t("RichEditor.Heading2")}</span>
            </DropDownItem>
            <DropDownItem
                className={"item " + dropDownActiveClass(blockType === "h3")}
                onClick={() => formatHeading("h3")}
            >
                {/* <i className="icon h3" /> */}
                <span className="text">{i18n.t("RichEditor.Heading3")}</span>
            </DropDownItem>
            <DropDownItem className={"item " + dropDownActiveClass(blockType === "bullet")} onClick={formatBulletList}>
                {/* <i className="icon bullet-list" /> */}
                <span className="text">{i18n.t("RichEditor.BulletList")}</span>
            </DropDownItem>
            <DropDownItem
                className={"item " + dropDownActiveClass(blockType === "number")}
                onClick={formatNumberedList}
            >
                {/* <i className="icon numbered-list" /> */}
                <span className="text">{i18n.t("RichEditor.NumberedList")}</span>
            </DropDownItem>
        </DropDown>
    );
}

function Divider(): JSX.Element {
    return <div className="divider" />;
}

function ElementFormatDropdown({
    editor,
    value,
    disabled = false,
}: {
    editor: LexicalEditor;
    value: ElementFormatType;
    disabled: boolean;
}) {
    const formatOption = ELEMENT_FORMAT_OPTIONS[value || "left"];
    const [t] = useTranslation();

    return (
        <DropDown
            disabled={disabled}
            buttonLabel={formatOption.name}
            buttonIconClassName={`icon {formatOption.icon}`}
            buttonClassName="toolbar-item spaced alignment"
            buttonAriaLabel="Formatting options for text alignment"
        >
            <DropDownItem
                onClick={() => {
                    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
                }}
                className="item"
            >
                <span className="text">{t("RichEditor.LeftAlign")}</span>
            </DropDownItem>
            <DropDownItem
                onClick={() => {
                    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center");
                }}
                className="item"
            >
                <span className="text">{t("RichEditor.CenterAlign")}</span>
            </DropDownItem>
            <DropDownItem
                onClick={() => {
                    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
                }}
                className="item"
            >
                <span className="text">{t("RichEditor.RightAlign")}</span>
            </DropDownItem>
            <DropDownItem
                onClick={() => {
                    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify");
                }}
                className="item"
            >
                <span className="text">{t("RichEditor.Justify")}</span>
            </DropDownItem>
        </DropDown>
    );
}

export default function ToolbarPlugin(props: {
    isAgreementSetup?: boolean;
    isAgreementEdit?: boolean;
    isAgreementSettings?: boolean;
    /**
     * Insert variable component
     */
    InsertVariablePlugin: () => JSX.Element;
}): JSX.Element {
    const {
        isAgreementSetup = false,
        isAgreementEdit = false,
        isAgreementSettings = false,
        InsertVariablePlugin,
    } = props;
    const { getCompanySettings } = useCompanySettings();
    const [editor] = useLexicalComposerContext();
    const [activeEditor, setActiveEditor] = useState(editor);
    const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>("paragraph");
    const [rootType, setRootType] = useState<keyof typeof rootTypeToRootName>("root");
    const [elementFormat, setElementFormat] = useState<ElementFormatType>("left");
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [isEditable, setIsEditable] = useState(() => editor.isEditable());
    const [isArticle, setIsArticle] = useState(false);
    const [isConditional, setIsConditional] = useState(false);
    const [isRepeatable, setIsRepeatable] = useState(false);
    const [isParagraph, setIsParagraph] = useState(false);
    const [isEmptyTextNode, setIsEmptyTextNode] = useState(false); // If it's a textNode but with empty textContent like --> "   "
    const [isImageSelected, setIsImageSelected] = useState(false);
    const [fontSize, setFontSize] = useState<string>("14px");
    const [showInsertImageDialog, setShowInsertImageDialog] = useState(false);
    const [t] = useTranslation();
    const usesAgreementValidation = getCompanySettings("uses_agreement_validation_chain");
    const portalType = useContext(PortalType);

    /**
     * Handle format a block -> create or remove it from the cursor selection
     * @param { "conditional" | "repeatable "} type If used for conditional block or repeatable block
     */
    const handleFormatBlock = (type: "conditional" | "repeatable" | "repeatable-article") => {
        editor.update(() => {
            const selection = $getSelection();
            const selectionNode = (selection as RangeSelection)?.anchor.getNode();

            const blockAttributes: Record<string, TBlockAttribute> = {
                conditional: {
                    nodeType: "conditional-content",
                    nodeParent:
                        $findMatchingParent(selectionNode, $isConditionalContentNode) ||
                        $findMatchingParent(selectionNode, $isConditionalNode),
                    insertAction: (node: ParagraphNode | LexicalNode[]) =>
                        editor.dispatchCommand(INSERT_CONDITIONAL_COMMAND, node),
                },
                repeatable: {
                    nodeType: "repeatable-content",
                    nodeParent: $findMatchingParent(selectionNode, $isRepeatableContentNode),
                    insertAction: (node: ParagraphNode | LexicalNode[]) =>
                        editor.dispatchCommand(INSERT_REPEATABLE_COMMAND, { nodes: node, repeatBy: "beneficiary" }),
                },
                "repeatable-article": {
                    nodeType: "repeatable-content",
                    nodeParent: $findMatchingParent(selectionNode, $isRepeatableContentNode),
                    insertAction: (node: ParagraphNode | LexicalNode[]) =>
                        editor.dispatchCommand(INSERT_REPEATABLE_COMMAND, { nodes: node, repeatBy: "article" }),
                },
            };

            const currentBlock = blockAttributes[type];
            const parentNode = currentBlock.nodeParent;

            // If node is not conditional/repeatable, create a conditional/repeatable node
            if (!parentNode) {
                const selectionNodes = selection?.getNodes();
                const isInlineSelection = !selectionNodes?.find((node) => $isParagraphNode(node)); // if the selection doesn't contain paragraph node

                if (isInlineSelection) {
                    const paragraph = $findMatchingParent(selectionNode, $isParagraphNode);
                    paragraph?.remove();
                    if (paragraph) currentBlock.insertAction(paragraph);
                } else {
                    // else select all paragprah in the selection
                    const selectionParagraph = selectionNodes?.filter((node) => node.__type === "paragraph");
                    selectionParagraph?.forEach((node) => node.remove());
                    if (selectionParagraph) currentBlock.insertAction(selectionParagraph);
                }
            } else {
                /**
                 * Else condition is to remove conditionnal or repeatable behavior to a container/conditional node
                 * it exist previous conditional nodes, now it's conditional containers nodes
                 * I need to check the type here
                 * */
                let blockParentNode = parentNode;
                if (parentNode.__type === currentBlock.nodeType) {
                    blockParentNode = parentNode.getParent() as TLexicalBlockType;
                }
                const blockContentNode =
                    blockParentNode.getChildren().find((child) => child.__type === currentBlock.nodeType) ??
                    blockParentNode;

                const blockParagraphs = (blockContentNode as ElementNode)?.getChildren();
                blockParentNode.remove();
                blockParagraphs.forEach((paragraphNode) => {
                    $insertNodeToNearestRoot(paragraphNode);
                    if (!paragraphNode.getNextSibling()?.getTextContent().length) {
                        paragraphNode.getNextSibling()?.remove();
                    }
                });
            }
        });
    };

    /**
     * Update toolbar from current active cursor selection
     */
    const $updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if (selection && $isNodeSelection(selection)) {
            const nodes = selection.getNodes();
            setIsImageSelected(nodes.length === 1 && $isImageNode(nodes[0]));
        } else {
            setIsImageSelected(false);
        }
        if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            const isConditional = !!$findMatchingParent(anchorNode, $isConditionalContainerNode);
            const isRepeatable = !!$findMatchingParent(anchorNode, $isRepeatableContainerNode);
            const isParagraph = $isParagraphNode(anchorNode);
            const isEmptyTextNode = anchorNode.getTextContent().replace(/\s/g, "").length === 0; // if the node is a text node and empty, it's not a paragraph

            setIsConditional(isConditional);
            setIsRepeatable(isRepeatable);
            setIsParagraph(isParagraph);
            setIsEmptyTextNode(isEmptyTextNode);

            let element =
                anchorNode.getKey() === "root"
                    ? anchorNode
                    : $findMatchingParent(anchorNode, (e) => {
                          const parent = e.getParent();
                          return parent !== null && $isRootOrShadowRoot(parent);
                      });

            if (element === null) {
                element = anchorNode.getTopLevelElementOrThrow();
            }
            if ($isArticleContainerNode(element) || $isArticleContentNode(element.getParent())) {
                setIsArticle(true);
            } else {
                setIsArticle(false);
            }
            const elementKey = element.getKey();
            const elementDOM = activeEditor.getElementByKey(elementKey);

            // Update text format
            setIsBold(selection.hasFormat("bold"));
            setIsItalic(selection.hasFormat("italic"));
            setIsUnderline(selection.hasFormat("underline"));
            setIsStrikethrough(selection.hasFormat("strikethrough"));

            // Update links
            const node = getSelectedNode(selection);
            const parent = node.getParent();

            const tableNode = $findMatchingParent(node, $isTableNode);
            if ($isTableNode(tableNode)) {
                setRootType("table");
            } else {
                setRootType("root");
            }

            if (elementDOM !== null) {
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
                    const type = parentList ? parentList.getListType() : element.getListType();
                    setBlockType(type);
                } else {
                    const type = $isHeadingNode(element) ? element.getTag() : element.getType();
                    if (type in blockTypeToBlockName) {
                        setBlockType(type as keyof typeof blockTypeToBlockName);
                    }
                }
            }
            let matchingParent;

            // If matchingParent is a valid node, pass it's format type
            setElementFormat(
                $isElementNode(matchingParent)
                    ? matchingParent.getFormatType()
                    : $isElementNode(node)
                      ? node.getFormatType()
                      : parent?.getFormatType() || "left",
            );

            if ($isRangeSelection(selection) || $isTableSelection(selection)) {
                setFontSize($getSelectionStyleValueForProperty(selection, "font-size", "14px"));
            }
        }
    }, [activeEditor]);

    useEffect(() => {
        return editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            (_payload, newEditor) => {
                $updateToolbar();
                setActiveEditor(newEditor);
                return false;
            },
            COMMAND_PRIORITY_CRITICAL,
        );
    }, [editor, $updateToolbar]);

    useEffect(() => {
        return mergeRegister(
            editor.registerEditableListener((editable) => {
                setIsEditable(editable);
            }),
            activeEditor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    $updateToolbar();
                });
            }),
            activeEditor.registerCommand<boolean>(
                CAN_UNDO_COMMAND,
                (payload) => {
                    setCanUndo(payload);
                    return false;
                },
                COMMAND_PRIORITY_CRITICAL,
            ),
            activeEditor.registerCommand<boolean>(
                CAN_REDO_COMMAND,
                (payload) => {
                    setCanRedo(payload);
                    return false;
                },
                COMMAND_PRIORITY_CRITICAL,
            ),
        );
    }, [$updateToolbar, activeEditor, editor]);

    const applyStyleText = useCallback(
        (styles: Record<string, string>, skipHistoryStack?: boolean) => {
            activeEditor.update(
                () => {
                    const selection = $getSelection();
                    if (selection !== null) {
                        $patchStyleText(selection, styles);
                    }
                },
                skipHistoryStack ? { tag: "historic" } : {},
            );
        },
        [activeEditor],
    );

    const blockActions: Action[] = [
        {
            name: t("RichEditor.NewOptional"),
            action: () => handleFormatBlock("conditional"),
        },
        {
            name: t("RichEditor.NewRepeatable"),
            action: () => handleFormatBlock("repeatable"),
            subtitle: t("RichEditor.MultiSignature"),
        },
    ];

    const clearFormatting = useCallback(() => {
        activeEditor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                const anchor = selection.anchor;
                const focus = selection.focus;
                const nodes = selection.getNodes();

                if (anchor.key === focus.key && anchor.offset === focus.offset) {
                    return;
                }

                nodes.forEach((node, idx) => {
                    // We split the first and last node by the selection
                    // So that we don't format unselected text inside those nodes
                    if ($isTextNode(node)) {
                        // Use a separate variable to ensure TS does not lose the refinement
                        let textNode = node;
                        if (idx === 0 && anchor.offset !== 0) {
                            textNode = textNode.splitText(anchor.offset)[1] || textNode;
                        }
                        if (idx === nodes.length - 1) {
                            textNode = textNode.splitText(focus.offset)[0] || textNode;
                        }

                        if (textNode.__style !== "") {
                            textNode.setStyle("");
                        }
                        if (textNode.__format !== 0) {
                            textNode.setFormat(0);
                            $getNearestBlockElementAncestorOrThrow(textNode).setFormat("");
                        }
                        node = textNode;
                    } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
                        node.replace($createParagraphNode(), true);
                    } else if ($isDecoratorBlockNode(node)) {
                        node.setFormat("");
                    }
                });
            }
        });
    }, [activeEditor]);

    const onFontColorSelect = useCallback(
        (value: string, skipHistoryStack: boolean) => {
            applyStyleText({ color: value }, skipHistoryStack);
        },
        [applyStyleText],
    );

    const onBgColorSelect = useCallback(
        (value: string, skipHistoryStack: boolean) => {
            applyStyleText({ "background-color": value }, skipHistoryStack);
        },
        [applyStyleText],
    );

    const handleInsertAppendix = () => {
        activeEditor.update(() => {
            const selection = $getSelection();
            activeEditor.dispatchCommand(INSERT_PAGE_BREAK, undefined);
            if (selection) {
                const headingNode = $createHeadingNode("h2");
                const textNode = $createTextNode(`${t("ContractSettings.Appendix")}`);
                textNode.toggleFormat("bold");
                headingNode.append(textNode);
                headingNode.setFormat("center");
                $insertNodes([headingNode]);
                (selection as RangeSelection).formatText("bold");
            }
        });
    };

    const showReverseFormatBtn = isConditional || isRepeatable;

    return isAgreementSetup ? (
        <></>
    ) : (
        <div className={classNames({ toolbar: true, "sticky-top": isAgreementSettings })}>
            <button
                disabled={!canUndo || !isEditable}
                onClick={() => {
                    activeEditor.dispatchCommand(UNDO_COMMAND, undefined);
                }}
                title={IS_APPLE ? "Undo (⌘Z)" : "Undo (Ctrl+Z)"}
                type="button"
                className="toolbar-item spaced"
                aria-label="Undo"
            >
                <ReplayOutlinedIcon sx={{ width: "16px !important" }} />
            </button>
            <button
                disabled={!canRedo || !isEditable}
                onClick={() => {
                    activeEditor.dispatchCommand(REDO_COMMAND, undefined);
                }}
                title={IS_APPLE ? "Redo (⌘Y)" : "Redo (Ctrl+Y)"}
                type="button"
                className="toolbar-item"
                aria-label="Redo"
            >
                <ReplayOutlinedIcon sx={{ width: "16px !important", transform: "scaleX(-1)" }} />
            </button>
            <Divider />
            {!isAgreementEdit && blockType in blockTypeToBlockName && activeEditor === editor && (
                <>
                    <BlockFormatDropDown
                        disabled={!isEditable || isImageSelected}
                        blockType={blockType}
                        rootType={rootType}
                        editor={editor}
                    />
                    <Divider />
                </>
            )}
            <>
                <FontSize
                    editor={activeEditor}
                    disabled={!isEditable || isImageSelected}
                    selectionFontSize={fontSize.slice(0, -2)}
                />
                <Divider />
                <ToolbarButton
                    disabled={!isEditable || isImageSelected}
                    onClick={() => {
                        activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
                    }}
                    className={isBold ? "active" : ""}
                    title={IS_APPLE ? `${t("RichEditor.Bold")} (⌘B)` : `${t("RichEditor.Bold")} (Ctrl+B)`}
                >
                    <FormatBoldIcon />
                </ToolbarButton>
                <ToolbarButton
                    disabled={!isEditable || isImageSelected}
                    onClick={() => {
                        activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
                    }}
                    className={isItalic ? "active" : ""}
                    title={IS_APPLE ? `${t("RichEditor.Italic")} (⌘I)` : `${t("RichEditor.Italic")} (Ctrl+I)`}
                >
                    <FormatItalicIcon />
                </ToolbarButton>
                <ToolbarButton
                    disabled={!isEditable || isImageSelected}
                    onClick={() => {
                        activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
                    }}
                    className={isUnderline ? "active" : ""}
                    title={IS_APPLE ? `${t("RichEditor.Underline")} (⌘U)` : `${t("RichEditor.Underline")} (Ctrl+U)`}
                >
                    <FormatUnderlinedIcon />
                </ToolbarButton>
                <ToolbarButton
                    disabled={!isEditable || isImageSelected}
                    onClick={() => {
                        activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
                    }}
                    className={dropDownActiveClass(isStrikethrough)}
                    title={t("RichEditor.Strikethrough")}
                >
                    <StrikethroughSIcon />
                </ToolbarButton>
            </>

            {!isAgreementEdit && (
                <>
                    <Divider />
                    <ToolbarButton
                        disabled={!isEditable}
                        title={t("RichEditor.LeftAlign")}
                        onClick={() => {
                            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "left");
                        }}
                    >
                        <FormatAlignLeftIcon />
                    </ToolbarButton>
                    <ToolbarButton
                        disabled={!isEditable}
                        title={t("RichEditor.CenterAlign")}
                        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "center")}
                    >
                        <FormatAlignCenterIcon />
                    </ToolbarButton>
                    <ToolbarButton
                        disabled={!isEditable}
                        title={t("RichEditor.RightAlign")}
                        onClick={() => {
                            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "right");
                        }}
                    >
                        <FormatAlignRightIcon />
                    </ToolbarButton>
                    <ToolbarButton
                        disabled={!isEditable}
                        title={t("RichEditor.Justify")}
                        onClick={() => {
                            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, "justify");
                        }}
                    >
                        <FormatAlignJustifyIcon />
                    </ToolbarButton>
                    <Divider />
                    <DropDown
                        disabled={!isEditable || isImageSelected}
                        buttonClassName="toolbar-item spaced"
                        buttonLabel={t("ContractSettings.Insert")}
                        showExpandIcon
                    >
                        <DropDownItem
                            onClick={() => {
                                activeEditor.dispatchCommand(INSERT_PAGE_BREAK, undefined);
                            }}
                            className="item"
                        >
                            <InsertPageBreakOutlinedIcon sx={{ width: "16px" }} />
                            <span className="text">{t("ContractSettings.PageBreak")}</span>
                        </DropDownItem>
                        <DropDownItem onClick={() => setShowInsertImageDialog(true)} className="item">
                            <ImageOutlinedIcon sx={{ width: "16px" }} />
                            <span className="text">{t("ContractSettings.InsertImage")}</span>
                        </DropDownItem>
                        <DropDownItem onClick={handleInsertAppendix} className="item">
                            <NoteAddOutlinedIcon sx={{ width: "16px" }} />
                            <span className="text">{t("ContractSettings.Appendix")}</span>
                        </DropDownItem>
                    </DropDown>
                    <InsertImageDialog
                        open={showInsertImageDialog}
                        onClose={() => setShowInsertImageDialog(false)}
                        onConfirm={(payload) => {
                            activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
                            // Refocus editor after insert so image selection works immediately
                            requestAnimationFrame(() => activeEditor.focus());
                        }}
                    />
                    <Divider />
                    <ToolbarButton
                        disabled={
                            !isEditable ||
                            isImageSelected ||
                            (!isParagraph && !isEmptyTextNode) ||
                            isRepeatable ||
                            isConditional
                        }
                        onClick={() => {
                            editor.focus();
                            editor.dispatchCommand(INSERT_ARTICLE_COMMAND, { getCurrentSelection: false });
                        }}
                    >
                        <AddOutlinedIcon sx={{ width: "16px !important" }} /> Article
                    </ToolbarButton>
                    <Divider />
                    {showReverseFormatBtn ? (
                        <button
                            className={"toolbar-item spaced "}
                            disabled={!isEditable || isImageSelected}
                            onClick={() => {
                                isConditional ? handleFormatBlock("conditional") : handleFormatBlock("repeatable");
                            }}
                        >
                            <div className="format strikethrough">
                                <CloseOutlinedIcon sx={{ width: "14px !important", color: "var(--danger-text)" }} />
                                {isConditional ? "Optionnel" : "Répétable"}
                            </div>
                        </button>
                    ) : (
                        <>
                            {usesAgreementValidation && (
                                <DropDown
                                    disabled={!isEditable || isImageSelected}
                                    buttonClassName="toolbar-item spaced"
                                    showExpandIcon
                                    buttonLabel="Bloc"
                                >
                                    <DropDownItem
                                        className="item block-menu-item"
                                        onClick={() => handleFormatBlock("conditional")}
                                    >
                                        <AddOutlinedIcon sx={{ width: "16px", height: "16px" }} />
                                        <span className="text">{t("RichEditor.NewOptional")}</span>
                                    </DropDownItem>
                                    {!["press", "audiovisual"].includes(portalType) && (
                                        <>
                                            <DropDownItem
                                                className="item block-menu-item"
                                                onClick={() => handleFormatBlock("repeatable")}
                                            >
                                                <AddOutlinedIcon sx={{ width: "16px", height: "16px" }} />
                                                <span className="text">{t("RichEditor.RepeatableByBeneficiary")}</span>
                                            </DropDownItem>
                                            <DropDownItem
                                                className="item block-menu-item"
                                                onClick={() => handleFormatBlock("repeatable-article")}
                                            >
                                                <AddOutlinedIcon sx={{ width: "16px", height: "16px" }} />
                                                <span className="text">{t("RichEditor.RepeatableByArticle")}</span>
                                            </DropDownItem>
                                        </>
                                    )}
                                </DropDown>
                            )}
                        </>
                    )}
                </>
            )}
            {!isAgreementSetup && !isAgreementEdit && <InsertVariablePlugin />}
        </div>
    );
}

/**
 * UNDO BTN CODE EXEMPLE
 */
{
    /* <>
    <button
        disabled={!canUndo || !isEditable}
        onClick={() => {
            activeEditor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        title={IS_APPLE ? "Undo (⌘Z)" : "Undo (Ctrl+Z)"}
        type="button"
        className="toolbar-item spaced"
        aria-label="Undo"
    >
        <i className="format undo" />
    </button>
    <button
        disabled={!canRedo || !isEditable}
        onClick={() => {
            activeEditor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        title={IS_APPLE ? "Redo (⌘Y)" : "Redo (Ctrl+Y)"}
        type="button"
        className="toolbar-item"
        aria-label="Redo"
    >
        <i className="format redo" />
    </button>
    <Divider />
</>; */
}
